import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { ClientBasicInfo } from "./ClientBasicInfo";
import { ClientDocuments } from "./ClientDocuments";
import { ClientCalculations } from "./ClientCalculations";
import { ClientTimeline } from "./ClientTimeline";
import { ClientPaymentPlan } from "./payment-plan/ClientPaymentPlan";
import { ClientNotes } from "./ClientNotes";
import { ClientForm } from "../ClientForm";
import { AdminSidebar } from "@/components/AdminSidebar";
import { DashboardHeader } from "@/components/DashboardHeader";
import { DashboardFooter } from "@/components/DashboardFooter";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useToast } from "@/hooks/use-toast";

interface ClientDetailsProps {
  mode?: 'view' | 'edit';
}

export const ClientDetails = ({ mode = 'view' }: ClientDetailsProps) => {
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<any>(null);
  const [paymentPlan, setPaymentPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchClientAndPaymentPlan = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const [clientResponse, paymentPlanResponse] = await Promise.all([
          supabase
            .from('clients')
            .select('*')
            .eq('id', id)
            .single(),
          supabase
            .from('payment_plans')
            .select('*')
            .eq('client_id', id)
            .maybeSingle()
        ]);

        if (clientResponse.error) {
          console.error('Error fetching client:', clientResponse.error);
          throw new Error(clientResponse.error.message);
        }

        if (!clientResponse.data) {
          throw new Error('Client not found');
        }

        setClient(clientResponse.data);
        
        if (!paymentPlanResponse.error) {
          setPaymentPlan(paymentPlanResponse.data);
        }
      } catch (error: any) {
        console.error('Error fetching data:', error);
        setError(error.message);
        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to load client: ${error.message}`
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchClientAndPaymentPlan();
    }
  }, [id, toast]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
          <p className="text-lg text-red-500">{error}</p>
          <button 
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      );
    }

    if (!client) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
          <p className="text-lg text-white">Client not found.</p>
          <button 
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      );
    }

    if (mode === 'edit') {
      return <ClientForm client={client} mode="edit" />;
    }

    return (
      <div className="space-y-6">
        <ClientBasicInfo client={client} mode={mode} />
        <ClientNotes clientId={client.id} />
        <ClientDocuments documents={client.documents || []} clientId={client.id} />
        <ClientCalculations calculations={client.calculations || []} />
        <ClientTimeline 
          client={client}
          calculations={client.calculations || []}
          documents={client.documents || []}
        />
        <ClientPaymentPlan 
          clientId={client.id} 
          existingPlan={paymentPlan}
          mode="admin"
        />
      </div>
    );
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-[#1A1F2C] dark:bg-[#0F1219]">
        <AdminSidebar />
        <div className="flex-1">
          <DashboardHeader />
          <main className="pt-16 min-h-[calc(100vh-64px)]">
            <div className="container mx-auto p-8">
              {renderContent()}
            </div>
          </main>
          <DashboardFooter />
        </div>
      </div>
    </SidebarProvider>
  );
};